import Fetch from '../fetch'
import { signMessage } from '@wagmi/core'
import { finishLoginIntent } from './loginIntent'

const SESSION_STORAGE_KEY = 'walletLoginSessions'
const SUPPORTED_CHAINS = [1, 56]

const normalizeAddress = (value) => {
  const address = typeof value === 'string' ? value.trim().toLowerCase() : ''
  return /^0x[a-f0-9]{40}$/.test(address) ? address : ''
}

const parseChainId = (value) => {
  const chainId = Number(String(value).split(':').pop())
  return SUPPORTED_CHAINS.includes(chainId) ? chainId : 0
}

const decodeJwt = (token) => {
  try {
    const payload = token.split('.')[1]
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(window.atob(normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=')))
  } catch (error) {
    return null
  }
}

const isTokenValid = (token) => {
  const payload = decodeJwt(token || '')
  return Boolean(payload && Number(payload.exp) * 1000 > Date.now())
}

const readSessions = () => {
  try {
    const sessions = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '[]')
    return Array.isArray(sessions) ? sessions.filter((session) => session && isTokenValid(session.token)) : []
  } catch (error) {
    return []
  }
}

const writeSessions = (sessions) => {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions))
}

const getReferrerId = () => {
  const value = localStorage.getItem('inviter') || localStorage.getItem('t_mobile') || ''
  return /^\d+$/.test(value) ? Number(value) : 0
}

const getCurrentOrigin = () => {
  if (typeof window === 'undefined' || !window.location.origin) {
    throw new Error('Wallet login origin is unavailable')
  }
  return window.location.origin
}

const formatMessage = ({ domain, uri, version, nonce, issuedAt, expirationTime, accountAddress, chainId }) => {
  const numericChainId = parseChainId(chainId)
  return `${domain} wants you to sign in with your Ethereum account:\n${accountAddress}\n\nURI: ${uri}\nVersion: ${version}\nChain ID: ${numericChainId}\nNonce: ${nonce}\nIssued At: ${issuedAt}\nExpiration Time: ${expirationTime}`
}

const activeSession = (address, chainId) => {
  const normalizedAddress = normalizeAddress(address)
  return readSessions().find((session) => session.address === normalizedAddress && session.chainId === chainId)
}

const activeTokenSession = (address, chainId) => {
  const normalizedAddress = normalizeAddress(address)
  const token = localStorage.getItem('token') || ''
  if (!normalizedAddress || !SUPPORTED_CHAINS.includes(chainId) || !isTokenValid(token)) return null
  if (normalizeAddress(localStorage.getItem('userAddress')) !== normalizedAddress) return null
  return {
    data: {
      accountAddress: normalizedAddress,
      chainId: `eip155:${chainId}`,
    },
    message: '',
    signature: '',
  }
}

const storeSession = (session, token, makeActive = true) => {
  const address = normalizeAddress(session.data.accountAddress)
  const chainId = parseChainId(session.data.chainId)
  if (!address || !chainId || !isTokenValid(token)) throw new Error('Invalid wallet login session')

  const savedSession = {
    address,
    chainId,
    token,
    data: session.data,
    message: session.message,
    signature: session.signature,
  }
  const sessions = readSessions().filter((item) => item.address !== address || item.chainId !== chainId)
  sessions.push(savedSession)
  writeSessions(sessions)
  if (makeActive) {
    localStorage.setItem('token', token)
    localStorage.setItem('userAddress', address)
  }
}

const verifySession = async (session, makeActive = true) => {
  const address = normalizeAddress(session.data && session.data.accountAddress)
  const chainId = parseChainId(session.data && session.data.chainId)
  if (!address || !chainId || !session.message || !session.signature) throw new Error('Invalid SIWX session')

  const verified = await Fetch('/index/wallet_login_verify', {
    address,
    chainId,
    nonce: session.data.nonce,
    signature: session.signature,
    message: session.message,
  }, { timeout: 12000 })
  const token = verified && verified.code === 1 && verified.data ? verified.data.token : ''
  if (!token) throw new Error('Wallet login failed')
  storeSession(session, token, makeActive)
}

const getChallenge = async (accountAddress, chainId) => {
  const challenge = await Fetch('/index/wallet_login_challenge', {
    address: accountAddress,
    chainId,
    referrerId: getReferrerId(),
    origin: getCurrentOrigin(),
  }, { timeout: 12000 })
  if (!challenge || challenge.code !== 1 || !challenge.data || !challenge.data.nonce) {
    throw new Error('Unable to create wallet login challenge')
  }
  return challenge.data
}

export const signInWithWallet = async (walletConfig, accountAddress, chainId) => {
  const address = normalizeAddress(accountAddress)
  const numericChainId = parseChainId(chainId)
  if (!address || !numericChainId) throw new Error('Unsupported network')

  const challenge = await getChallenge(address, numericChainId)
  const data = {
    accountAddress: address,
    chainId: `eip155:${numericChainId}`,
    domain: challenge.domain,
    uri: challenge.uri,
    version: challenge.version,
    nonce: challenge.nonce,
    issuedAt: challenge.issuedAt,
    expirationTime: challenge.expirationTime,
  }
  const message = challenge.message || formatMessage(data)
  const signature = await signMessage(walletConfig, { message })
  await verifySession({ data, message, signature })
}

export const restoreWalletLogin = (address, chainId) => {
  const session = activeSession(address, chainId)
  if (!session) return false
  localStorage.setItem('token', session.token)
  localStorage.setItem('userAddress', session.address)
  return true
}

export const clearWalletSessions = (address = '') => {
  const normalizedAddress = normalizeAddress(address)
  if (!normalizedAddress) {
    localStorage.removeItem(SESSION_STORAGE_KEY)
    return
  }
  writeSessions(readSessions().filter((session) => session.address !== normalizedAddress))
}

export const siwx = {
  createMessage: async ({ accountAddress, chainId }) => {
    const address = normalizeAddress(accountAddress)
    const numericChainId = parseChainId(chainId)
    if (!numericChainId) throw new Error('Unsupported network')
    const challenge = await getChallenge(address, numericChainId)
    const data = {
      accountAddress: address,
      chainId: `eip155:${numericChainId}`,
      domain: challenge.domain,
      uri: challenge.uri,
      version: challenge.version,
      nonce: challenge.nonce,
      issuedAt: challenge.issuedAt,
      expirationTime: challenge.expirationTime,
    }
    return {
      ...data,
      toString: () => challenge.message || formatMessage(data),
    }
  },
  addSession: async (session) => {
    await verifySession(session)
    finishLoginIntent({ loggedIn: true, updated: true })
  },
  revokeSession: async (chainId, address) => {
    const numericChainId = parseChainId(chainId)
    const normalizedAddress = normalizeAddress(address)
    const sessions = readSessions().filter((session) => session.address !== normalizedAddress || session.chainId !== numericChainId)
    writeSessions(sessions)
  },
  setSessions: async (sessions) => {
    if (!Array.isArray(sessions)) throw new Error('Invalid SIWX sessions')
    if (sessions.length === 0) {
      // AppKit can report an empty SIWX list while restoring its connector.
      // Explicit logout and DISCONNECT_SUCCESS clear these values elsewhere;
      // deleting them here would turn a page refresh into a fresh sign-in.
      return
    }
    for (let index = 0; index < sessions.length; index += 1) {
      await verifySession(sessions[index], index === 0)
    }
    const sessionKeys = sessions.map((session) => {
      const address = normalizeAddress(session.data && session.data.accountAddress)
      return `${address}:${parseChainId(session.data && session.data.chainId)}`
    })
    writeSessions(readSessions().filter((session) => sessionKeys.includes(`${session.address}:${session.chainId}`)))
    finishLoginIntent({ loggedIn: true, updated: true })
  },
  getSessions: async (chainId, address) => {
    const numericChainId = parseChainId(chainId)
    const session = activeSession(address, numericChainId)
    if (session) {
      return [{ data: session.data, message: session.message, signature: session.signature || '' }]
    }

    // Sessions issued before the SIWX cache was introduced still have a
    // valid system JWT. Treat that token as the existing authenticated
    // session so refreshing does not prompt the same wallet to sign again.
    const tokenSession = activeTokenSession(address, numericChainId)
    return tokenSession ? [tokenSession] : []
  },
  getRequired: () => true,
  // A provider emits transient disconnect events while restoring a persisted
  // connection after reload. The application's explicit logout action clears
  // this cache; AppKit must not clear it during that restore cycle.
  signOutOnDisconnect: false,
}
